"use client"

import HomeDetailsWrapper from "@/features/homeowners/components/home-details-wrapper"
import FormTextInput from "@/shared/components/form-inputs/form-text-input"
import FormIntegerInput from "@/shared/components/form-inputs/form-integer-input"
import { SchemaType } from "@/shared/types/helpers.type"
import FormRadioInput from "../../../../shared/components/form-inputs/form-radio-input"
import FormSelectInput from "../../../../shared/components/form-inputs/form-select-input"
import FormTextareaInput from "../../../../shared/components/form-inputs/form-textarea-input"
import DateStampedTextarea from "../../../../shared/components/form-inputs/form-date-stamped-textarea-input"
import { residentTypeOptions } from "../../constants/options"
import { yesNoRejectedOptions, yesNoUnknownOptions, yesNoOptions } from "../../../../shared/constants/options"
import FormPhoneInput from "../../../../shared/components/form-inputs/form-phone-input"
import FormYesNoInput from "../../../../shared/components/form-inputs/form-yes-no-input"
import FormDateInput from "../../../../shared/components/form-inputs/form-date-input"
import { HomeWithHomeOwnerFormType } from "../../schema/home-with-home-owner.schema"
import { useFormContext, useWatch } from "react-hook-form"
import { StateCountryAutocomplete } from "@/shared/components/form-inputs/StateCountryAutocomplete"
import { useState, useEffect } from "react"
import React from "react"
import { Check, ChevronsUpDown, Info } from "lucide-react"
import { Button } from "@/shared/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle, DialogTrigger } from "@/shared/components/ui/dialog"
import { Textarea } from "@/shared/components/ui/textarea"
import { useSession } from "@/features/auth/context/session.context"
import { UserRole } from "@/shared/constants/user-roles"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/shared/components/ui/tooltip"
import { getDisplayEmailValue, isNoEmailPlaceholderValue, NO_EMAIL_AVAILABLE_OPTION } from "@/features/volunteers/schemas/volunteers.schema"
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/shared/components/ui/form"
import { Input } from "@/shared/components/ui/new-input"
import { Popover, PopoverAnchor, PopoverContent } from "@/shared/components/ui/popover"
import { cn } from "@/shared/lib/utils"
import NoEmailInfoIcon from "@/shared/components/no-email-info-icon"

const BASE_FIELD_NAME = "homeOwner"

type LocationDetails = {
  city: string;
  state: string;
  country: string;
  latitude?: number;
  longitude?: number;
  zipCode?: any;
};


const PRAYER_REQUESTS_HELP_TEXT_KEY = 'prayer-requests-help-text'
const SAFETY_CONCERNS_HELP_TEXT_KEY = 'safety-concerns-help-text'
const OWNER_STORY_HELP_TEXT_KEY = 'owner-story-help-text'

type HomeOwnerFormProps = {
  homeId?: number | null
  isUpdateMode?: boolean
  /** When true (e.g. placeholder email), email starts unlocked for adding a real address. */
  canEditEmailOnUpdate?: boolean
}

function HomeOwnerForm({ homeId, isUpdateMode = false, canEditEmailOnUpdate = false }: HomeOwnerFormProps) {
    const { control, setValue, getValues } = useFormContext<HomeWithHomeOwnerFormType>()
    const emailValue = useWatch({ control, name: `${BASE_FIELD_NAME}.email` })
    const { session } = useSession()
    const [locationDetails, setLocationDetails] = useState<LocationDetails | null>(null);
    const [prayerRequestsDialogOpen, setPrayerRequestsDialogOpen] = useState(false)
    const [prayerRequestsHelpText, setPrayerRequestsHelpText] = useState<string>('')
    const [editPrayerRequestsHelpText, setEditPrayerRequestsHelpText] = useState<string>('')
    
    const [safetyConcernsDialogOpen, setSafetyConcernsDialogOpen] = useState(false)
    const [safetyConcernsHelpText, setSafetyConcernsHelpText] = useState<string>('')
    const [editSafetyConcernsHelpText, setEditSafetyConcernsHelpText] = useState<string>('')
    
    const [ownerStoryDialogOpen, setOwnerStoryDialogOpen] = useState(false)
    const [ownerStoryHelpText, setOwnerStoryHelpText] = useState<string>('')
    const [editOwnerStoryHelpText, setEditOwnerStoryHelpText] = useState<string>('')
    const [isAddEmailDropdownOpen, setIsAddEmailDropdownOpen] = useState(false)
    const [isEmailEditing, setIsEmailEditing] = useState(canEditEmailOnUpdate)
    const [emailValueBeforeEdit, setEmailValueBeforeEdit] = useState<string>('')
    const [emailEditConfirmOpen, setEmailEditConfirmOpen] = useState(false)
    const emailLabelNode = (
        <div className="flex items-center gap-1">
            <span>Email Address *</span>
            <NoEmailInfoIcon />
        </div>
    )
    
    const isAdmin = session?.role === UserRole.ADMIN
    const canManageEmail =
        session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER
    const showEmailAsEditable = !isUpdateMode || isEmailEditing

    const beginEmailEdit = () => {
        const currentEmail = String(getValues(`${BASE_FIELD_NAME}.email`) ?? '')
        setEmailValueBeforeEdit(currentEmail)
        setIsEmailEditing(true)
        if (isNoEmailPlaceholderValue(currentEmail)) {
            setValue(`${BASE_FIELD_NAME}.email`, '', {
                shouldDirty: false,
                shouldValidate: true,
            })
        }
        setEmailEditConfirmOpen(false)
    }

    const cancelEmailEdit = () => {
        setValue(`${BASE_FIELD_NAME}.email`, emailValueBeforeEdit, {
            shouldDirty: false,
            shouldValidate: true,
        })
        setIsEmailEditing(false)
        setIsAddEmailDropdownOpen(false)
    }

    // Load help text from localStorage on mount
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const storedPrayerRequests = localStorage.getItem(PRAYER_REQUESTS_HELP_TEXT_KEY)
            if (storedPrayerRequests) {
                setPrayerRequestsHelpText(storedPrayerRequests)
            }
            
            const storedSafetyConcerns = localStorage.getItem(SAFETY_CONCERNS_HELP_TEXT_KEY)
            if (storedSafetyConcerns) {
                setSafetyConcernsHelpText(storedSafetyConcerns)
            }
            
            const storedOwnerStory = localStorage.getItem(OWNER_STORY_HELP_TEXT_KEY)
            if (storedOwnerStory) {
                setOwnerStoryHelpText(storedOwnerStory)
            }
        }
    }, [])

    // Save prayer requests help text to localStorage
    const handleSavePrayerRequestsHelpText = () => {
        if (typeof window !== 'undefined') {
            localStorage.setItem(PRAYER_REQUESTS_HELP_TEXT_KEY, editPrayerRequestsHelpText)
            setPrayerRequestsHelpText(editPrayerRequestsHelpText)
            setPrayerRequestsDialogOpen(false)
        }
    }

    // Save safety concerns help text to localStorage
    const handleSaveSafetyConcernsHelpText = () => {
        if (typeof window !== 'undefined') {
            localStorage.setItem(SAFETY_CONCERNS_HELP_TEXT_KEY, editSafetyConcernsHelpText)
            setSafetyConcernsHelpText(editSafetyConcernsHelpText)
            setSafetyConcernsDialogOpen(false)
        }
    }

    // Load current help text when dialog opens
    useEffect(() => {
        if (prayerRequestsDialogOpen) {
            setEditPrayerRequestsHelpText(prayerRequestsHelpText)
        }
    }, [prayerRequestsDialogOpen, prayerRequestsHelpText])

    useEffect(() => {
        if (safetyConcernsDialogOpen) {
            setEditSafetyConcernsHelpText(safetyConcernsHelpText)
        }
    }, [safetyConcernsDialogOpen, safetyConcernsHelpText])
    
    useEffect(() => {
        if (ownerStoryDialogOpen) {
            setEditOwnerStoryHelpText(ownerStoryHelpText)
        }
    }, [ownerStoryDialogOpen, ownerStoryHelpText])
    
    // Save owner story help text to localStorage
    const handleSaveOwnerStoryHelpText = () => {
        if (typeof window !== 'undefined') {
            localStorage.setItem(OWNER_STORY_HELP_TEXT_KEY, editOwnerStoryHelpText)
            setOwnerStoryHelpText(editOwnerStoryHelpText)
            setOwnerStoryDialogOpen(false)
        }
    }
    
    // Define all field names in this form section
    const fieldNames = [
        "homeOwner.name",
        "home.address",
        "home.city",
        "home.state",
        "home.country",
        "home.zipCode",
        "home.latitude",
        "home.longitude",
        "homeOwner.phone",
        "home.county",
        "homeOwner.primaryLanguage",
        "homeOwner.email",
        "homeOwner.secondaryContact",
        "homeOwner.secondaryPhone",
        "homeOwner.dateOfBirth",
        "homeOwner.medicalNeeds",
        "homeOwner.isSingleParent",
        "homeOwner.numChildrenUnder18",
        "homeOwner.isFirstResponder",
        "homeOwner.isVeteran",
        "home.hasFloodInsurance",
        "homeOwner.residentType",
        "home.hasHomeInsurance",
        "homeOwner.appliedFEMA",
        "homeOwner.appliedSBA",
        "homeOwner.femaStatus",
        "homeOwner.sbaStatus",
        "homeOwner.safetyConcerns",
        "homeOwner.prayerRequest",
        "homeOwner.ownerStory"
    ]
    
    // Use useEffect to update form values when locationDetails changes
    useEffect(() => {
        if (locationDetails) {
            const dirtyOpts = { shouldDirty: true as const }
            setValue("home.city", locationDetails.city, dirtyOpts);
            setValue("home.zipCode", locationDetails.zipCode, dirtyOpts);
            setValue("home.state", locationDetails.state, dirtyOpts);
            setValue("home.latitude", locationDetails.latitude, dirtyOpts);
            setValue("home.longitude", locationDetails.longitude, dirtyOpts);
        }
    }, [locationDetails, setValue]);

    return (
        <div className="space-y-5">
        <HomeDetailsWrapper fieldNames={fieldNames} heading="Homeowner Details">
                <FormTextInput
                    inputGroupProps={{
                        label: "Homeowner Name *"
                    }}
                    placeholder="Homeowner Name"
                    name={`${BASE_FIELD_NAME}.name`}
                    control={control}
                />
            <StateCountryAutocomplete
                setLocationDetails={setLocationDetails}
                locationDetails={locationDetails}
                control={control}
                allowUnknownLocation={canManageEmail}
            />

            <FormPhoneInput
                inputGroupProps={{
                    label: "Phone Number *"
                }}
                placeholder="Phone Number"
                name={`${BASE_FIELD_NAME}.phone`}
                control={control}
            />
            {isUpdateMode && canManageEmail && !showEmailAsEditable ? (
                <FormField
                    control={control}
                    name={`${BASE_FIELD_NAME}.email`}
                    render={({ fieldState }) => (
                        <FormItem>
                            <FormLabel>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span>Email Address *</span>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        className="h-6 px-2 text-xs"
                                        onClick={() => setEmailEditConfirmOpen(true)}
                                    >
                                        Edit email
                                    </Button>
                                    <NoEmailInfoIcon />
                                </div>
                            </FormLabel>
                            <FormControl>
                                <Input
                                    type="text"
                                    value={getDisplayEmailValue(String(emailValue ?? ""))}
                                    readOnly
                                    tabIndex={0}
                                    className="bg-muted text-foreground opacity-100 cursor-default"
                                    aria-readonly="true"
                                />
                            </FormControl>
                            <FormMessage>{fieldState.error?.message}</FormMessage>
                            <Dialog open={emailEditConfirmOpen} onOpenChange={setEmailEditConfirmOpen}>
                                <DialogContent className="max-w-md">
                                    <DialogTitle>Change homeowner email?</DialogTitle>
                                    <DialogDescription>
                                        Changing this email updates the homeowner&apos;s login identity.
                                        Make sure the new address is correct before saving.
                                    </DialogDescription>
                                    <DialogFooter className="gap-2 sm:gap-0">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => setEmailEditConfirmOpen(false)}
                                        >
                                            Cancel
                                        </Button>
                                        <Button type="button" onClick={beginEmailEdit}>
                                            Continue editing
                                        </Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>
                        </FormItem>
                    )}
                />
            ) : showEmailAsEditable ? (
                <FormField
                    control={control}
                    name={`${BASE_FIELD_NAME}.email`}
                    render={({ field, fieldState }) => (
                        <FormItem>
                            <FormLabel>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span>Email Address *</span>
                                    {isUpdateMode && canManageEmail && (
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 px-2 text-xs"
                                            onClick={cancelEmailEdit}
                                        >
                                            Cancel email edit
                                        </Button>
                                    )}
                                    <NoEmailInfoIcon />
                                </div>
                            </FormLabel>
                            <Popover open={isAddEmailDropdownOpen} onOpenChange={setIsAddEmailDropdownOpen}>
                                <PopoverAnchor asChild>
                                    <div className="relative">
                                        <FormControl>
                                            <Input
                                                type="text"
                                                placeholder={isUpdateMode ? "Enter email address" : "Email Address"}
                                                value={getDisplayEmailValue(String(field.value ?? ""))}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                onFocus={() => {
                                                    if (isUpdateMode && isNoEmailPlaceholderValue(String(field.value ?? ""))) {
                                                        field.onChange("")
                                                    }
                                                    setIsAddEmailDropdownOpen(true)
                                                }}
                                                onClick={() => setIsAddEmailDropdownOpen(true)}
                                                onBlur={field.onBlur}
                                                variant={fieldState.error ? "destructive" : "default"}
                                            />
                                        </FormControl>
                                        <button
                                            type="button"
                                            className="absolute right-2 top-1/2 -translate-y-1/2"
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => setIsAddEmailDropdownOpen((prev) => !prev)}
                                        >
                                            <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                                        </button>
                                    </div>
                                </PopoverAnchor>
                                <PopoverContent
                                    className="p-1"
                                    align="start"
                                    onOpenAutoFocus={(e) => e.preventDefault()}
                                >
                                    <button
                                        type="button"
                                        className={cn(
                                            "flex w-full items-center gap-2 rounded-sm border-0 bg-transparent px-2 py-2 text-left text-sm shadow-none outline-none hover:bg-accent hover:text-accent-foreground focus:outline-none focus-visible:outline-none focus-visible:ring-0"
                                        )}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => {
                                            field.onChange(NO_EMAIL_AVAILABLE_OPTION)
                                            setIsAddEmailDropdownOpen(false)
                                        }}
                                    >
                                        <Check className={cn("h-4 w-4", String(field.value ?? "").toLowerCase() === NO_EMAIL_AVAILABLE_OPTION.toLowerCase() ? "opacity-100 text-primary" : "opacity-0")} />
                                        <span>{NO_EMAIL_AVAILABLE_OPTION}</span>
                                    </button>
                                </PopoverContent>
                            </Popover>
                            <FormMessage>{fieldState.error?.message}</FormMessage>
                        </FormItem>
                    )}
                />
            ) : (
                <FormField
                    control={control}
                    name={`${BASE_FIELD_NAME}.email`}
                    render={({ fieldState }) => (
                        <FormItem>
                            <FormLabel>{emailLabelNode}</FormLabel>
                            <FormControl>
                                <Input
                                    type="text"
                                    value={getDisplayEmailValue(String(emailValue ?? ""))}
                                    readOnly
                                    tabIndex={0}
                                    className="bg-muted text-foreground opacity-100 cursor-default"
                                    aria-readonly="true"
                                />
                            </FormControl>
                            <FormMessage>{fieldState.error?.message}</FormMessage>
                        </FormItem>
                    )}
                />
            )}
            <FormTextInput
                inputGroupProps={{
                    label: "County/Parish"
                }}
                placeholder="County/Parish"
                name="home.county"
                control={control}
            />
            <FormSelectInput
                inputGroupProps={{
                    label: "Resident Type"
                }}
                options={residentTypeOptions}
                name={`${BASE_FIELD_NAME}.residentType`}
                control={control}
            />
            <FormTextInput
                inputGroupProps={{
                    label: "Primary Language"
                }}
                placeholder="Primary Language"
                name={`${BASE_FIELD_NAME}.primaryLanguage`}
                control={control}
            />
            <FormTextInput
                inputGroupProps={{
                    label: "Secondary Contact"
                }}
                placeholder="Secondary Contact"
                name={`${BASE_FIELD_NAME}.secondaryContact`}
                control={control}
            />
            <FormPhoneInput
                inputGroupProps={{
                    label: "Secondary Phone Number"
                }}
                placeholder="Secondary Phone Number"
                name={`${BASE_FIELD_NAME}.secondaryPhone`}
                control={control}
            />
            <FormDateInput
                inputGroupProps={{
                    label: "Birthday of oldest resident in the home"
                }}
                placeholder="Select oldest resident birthday"
                name={`${BASE_FIELD_NAME}.dateOfBirth`}
                control={control}
                enableYearMonthDropdowns={true}
                fromYear={new Date().getFullYear() - 100}
                toYear={new Date().getFullYear()}
            />
            <FormTextInput
                inputGroupProps={{
                    label: "Special (Medical) Needs"
                }}
                placeholder="Special (Medical) Needs"
                name={`${BASE_FIELD_NAME}.medicalNeeds`}
                control={control}
            />
            <FormIntegerInput
                inputGroupProps={{
                    label: "Number of children under 18 yo in home"
                }}
                placeholder="Number of children under 18 yo in home"
                name={`${BASE_FIELD_NAME}.numChildrenUnder18`}
                control={control}
            />
            <FormYesNoInput
                inputGroupProps={{
                    label: "Is the resident a single parent or grandparent(s) raising children?"
                }}
                name={`${BASE_FIELD_NAME}.isSingleParent`}
                control={control}
            />
            <FormYesNoInput
                name={`${BASE_FIELD_NAME}.isFirstResponder`}
                inputGroupProps={{
                    label: "Is there a First Responder living in the home?"
                }}
                control={control}
            />
            <FormYesNoInput
                name={`${BASE_FIELD_NAME}.isVeteran`}
                inputGroupProps={{
                    label: "Is there a Veteran living in the home?"
                }}
                control={control}
            />
            <FormRadioInput
                inputGroupProps={{
                    label: "Does homeowner have Flood Insurance?"
                }}
                options={yesNoOptions}
                name="home.hasFloodInsurance"
                control={control}
            />
            <FormRadioInput
                inputGroupProps={{
                    label: "Does Homeowner have Homeowner's or Renter's Insurance"
                }}
                options={yesNoOptions}
                name="home.hasHomeInsurance"
                control={control}
            />
            <FormYesNoInput
                name={`${BASE_FIELD_NAME}.appliedFEMA`}
                inputGroupProps={{
                    label: "Has homeowner applied to FEMA?"
                }}
                control={control}
            />
            <FormYesNoInput
                name={`${BASE_FIELD_NAME}.appliedSBA`}
                inputGroupProps={{
                    label: "Has homeowner applied for SBA?"
                }}
                control={control}
            />
            <FormRadioInput
                inputGroupProps={{
                    label: "FEMA Received",
                }}
                options={yesNoRejectedOptions}
                name={`${BASE_FIELD_NAME}.femaStatus`}
                control={control}
            />
            <FormRadioInput
                inputGroupProps={{
                    label: "SBA Received?",
                }}
                options={yesNoRejectedOptions}
                name={`${BASE_FIELD_NAME}.sbaStatus`}
                control={control}
            />
            <div className="space-y-1">
                <div className="flex items-center gap-2">
                    <label className="text-sm pl-1">Safety Concerns/Personal Info</label>
                    {isAdmin && (
                        <Dialog open={safetyConcernsDialogOpen} onOpenChange={setSafetyConcernsDialogOpen}>
                            <DialogTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-4 w-4 p-0 text-muted-foreground hover:text-foreground"
                                >
                                    <Info className="h-4 w-4" />
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-[600px]">
                                <DialogTitle>Edit Safety Concerns/Personal Info Help Text</DialogTitle>
                                <DialogDescription>
                                    Edit the help text that will be displayed for the Safety Concerns/Personal Info field.
                                </DialogDescription>
                                <div className="space-y-4 py-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">Help Text</label>
                                        <Textarea
                                            value={editSafetyConcernsHelpText}
                                            onChange={(e) => setEditSafetyConcernsHelpText(e.target.value)}
                                            placeholder="Enter help text for Safety Concerns/Personal Info field..."
                                            rows={6}
                                        />
                                    </div>
                                    <div className="flex justify-end gap-2">
                                        <Button
                                            variant="outline"
                                            onClick={() => setSafetyConcernsDialogOpen(false)}
                                        >
                                            Cancel
                                        </Button>
                                        <Button onClick={handleSaveSafetyConcernsHelpText}>
                                            Save
                                        </Button>
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>
                    )}
                    {!isAdmin && safetyConcernsHelpText && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Info className="h-4 w-4 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-md">
                                    <p className="whitespace-pre-wrap">{safetyConcernsHelpText}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>
                {safetyConcernsHelpText && (
                    <p className="text-xs text-muted-foreground pl-1">{safetyConcernsHelpText}</p>
                )}
                <DateStampedTextarea
                    inputGroupProps={{
                        label: undefined, // Label is handled above
                    }}
                    rows={6}
                    className="min-h-[150px]"
                    name={`${BASE_FIELD_NAME}.safetyConcerns`}
                    control={control}
                />
            </div>
            <div className="space-y-1">
                <div className="flex items-center gap-2">
                    <label className="text-sm pl-1">Prayer Requests</label>
                    {isAdmin && (
                        <Dialog open={prayerRequestsDialogOpen} onOpenChange={setPrayerRequestsDialogOpen}>
                            <DialogTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-4 w-4 p-0 text-muted-foreground hover:text-foreground"
                                >
                                    <Info className="h-4 w-4" />
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-[600px]">
                                <DialogTitle>Edit Prayer Requests Help Text</DialogTitle>
                                <DialogDescription>
                                    Edit the help text that will be displayed for the Prayer Requests field.
                                </DialogDescription>
                                <div className="space-y-4 py-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">Help Text</label>
                                        <Textarea
                                            value={editPrayerRequestsHelpText}
                                            onChange={(e) => setEditPrayerRequestsHelpText(e.target.value)}
                                            placeholder="Enter help text for Prayer Requests field..."
                                            rows={6}
                                        />
                                    </div>
                                    <div className="flex justify-end gap-2">
                                        <Button
                                            variant="outline"
                                            onClick={() => setPrayerRequestsDialogOpen(false)}
                                        >
                                            Cancel
                                        </Button>
                                        <Button onClick={handleSavePrayerRequestsHelpText}>
                                            Save
                                        </Button>
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>
                    )}
                    {!isAdmin && prayerRequestsHelpText && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Info className="h-4 w-4 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-md">
                                    <p className="whitespace-pre-wrap">{prayerRequestsHelpText}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>
                {prayerRequestsHelpText && (
                    <p className="text-xs text-muted-foreground pl-1">{prayerRequestsHelpText}</p>
                )}
                <DateStampedTextarea
                    name={`${BASE_FIELD_NAME}.prayerRequest`}
                    control={control}
                    rows={6}
                    className="min-h-[150px]"
                    inputGroupProps={{
                        label: undefined, // Label is handled above
                    }}
                />
            </div>
            <div className="space-y-1">
                <div className="flex items-center gap-2">
                    <label className="text-sm pl-1 flex-1">Please share anything you feel comfortable about how the storm has affected your home or daily life. This helps us connect you with resources and support. No details are required.</label>
                    {isAdmin ? (
                        <Dialog open={ownerStoryDialogOpen} onOpenChange={setOwnerStoryDialogOpen}>
                            <DialogTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-4 w-4 p-0 text-muted-foreground hover:text-foreground flex-shrink-0 cursor-pointer relative z-10"
                                >
                                    <Info className="h-4 w-4" />
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-[600px]">
                                <DialogTitle>Edit Owner Story Help Text</DialogTitle>
                                <DialogDescription>
                                    Edit the help text that will be displayed for the Owner Story field.
                                </DialogDescription>
                                <div className="space-y-4 py-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">Help Text</label>
                                        <Textarea
                                            value={editOwnerStoryHelpText}
                                            onChange={(e) => setEditOwnerStoryHelpText(e.target.value)}
                                            placeholder="Enter help text for Owner Story field..."
                                            rows={6}
                                        />
                                    </div>
                                    <div className="flex justify-end gap-2">
                                        <Button
                                            variant="outline"
                                            onClick={() => setOwnerStoryDialogOpen(false)}
                                        >
                                            Cancel
                                        </Button>
                                        <Button onClick={handleSaveOwnerStoryHelpText}>
                                            Save
                                        </Button>
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>
                    ) : (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                {ownerStoryHelpText && (
                                    <TooltipContent className="max-w-md">
                                        <p className="whitespace-pre-wrap">{ownerStoryHelpText}</p>
                                    </TooltipContent>
                                )}
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>
                {ownerStoryHelpText && (
                    <p className="text-xs text-muted-foreground pl-1">{ownerStoryHelpText}</p>
                )}
                <DateStampedTextarea
                    inputGroupProps={{
                        label: undefined, // Label is handled above
                        wrapperClassName: "three-cols"
                    }}
                    rows={6}
                    className="min-h-[150px]"
                    name={`${BASE_FIELD_NAME}.ownerStory`}
                    control={control}
                />
            </div>
        </HomeDetailsWrapper>
    </div>
    )
}

export default HomeOwnerForm
